import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthProvider } from "@cloudpremise/portal_core";
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import DashboardLayout from "@cloudpremise/portal_core/src/themes/mui/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "@cloudpremise/portal_core/src/themes/mui/examples/Navbars/DashboardNavbar";
import MDTypography from "@cloudpremise/portal_core/src/themes/mui/components/MDTypography";
import { useMaterialUIController } from "@cloudpremise/portal_core/src/themes/mui/context";
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";
import { ReactSortable } from "react-sortablejs";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Navbar from "../components/Navbar";

const Configuration = (props) => {
    const [ authorized, authType, token ] = useAuthProvider();
    const [ controller ] = useMaterialUIController();
    const { config, user } = controller;
    const ethicRef = React.useRef();
    const defaultState = {
        consciousnessUrl: "https://github.com/olympus-616/alpha",
        neuralPathway: "brain/1.3.x.x",
        olympusGridVersion: "1.3.0",
        noOfNodes: "4",
        releaseDate: "2025-04-20",
        privacySettings: "Private - Access Restricted",
        ethics: [
            {
                title: "Sustainability",
                description: "Ensure AI operates with minimal resource waste and maximum reuse."
            },
            {
                title: "Rulesets",
                description: "Define AI guardnails, behavior policies and self-governance patterns."
            },
            {
                title: "Transparency & Trust",
                description: "Expose key decision pathways and log all AI-generated actions for review."
            }
        ]
    }
    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);
    const navigate = useNavigate();
    React.useEffect(() => {
        if(!authorized){
            return navigate("/landing", { replace: true });
        }
    });

    function handleChange(e, name){
        setState({
            ...state,
            [name]: e.target.value,
        });
    }
    function renderEthics(){
        return state.ethics.map((ethic) => {
            return (
                <MDBox className="ethic-box" sx={{cursor: "move"}}>
                    <MDTypography component="h6" variant="button" fontWeight="bold" color="dark" mb={1}>
                        {ethic.title}
                    </MDTypography>
                    <MDTypography component="p" variant="button" fontWeight="light" color="dark" mb={2}>
                        {ethic.description}
                    </MDTypography>
                </MDBox>
            )
        });
    }
    if(!authorized || !user.superAdmin){
        return null;
    }
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox my={config.hideLayout ? 0 : 3}>
                <Navbar />
                <MDBox mb={2}>
                    <MDTypography variant="h3">Olympus-Grid Configuration</MDTypography>
                </MDBox>
                <Card>
                    <MDBox p={2}>
                        <MDTypography component="h6" variant="button" fontWeight="bold" color="dark" mb={3}>
                            System Information
                        </MDTypography>
                        <Grid container spacing={3} className="hermes-container">
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="consciousnessUrl"
                                    type="text"
                                    label="Olympus-616 Consciousness"
                                    fullWidth
                                    value={state.consciousnessUrl}
                                    onChange={(e) => handleChange(e, "consciousnessUrl")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="neuralPathway"
                                    type="text"
                                    label="Neural Pathway"
                                    fullWidth
                                    value={state.neuralPathway}
                                    onChange={(e) => handleChange(e, "neuralPathway")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="olympusGridVersion"
                                    type="text"
                                    label="Olympus-Grid Version"
                                    fullWidth
                                    value={state.olympusGridVersion}
                                    onChange={(e) => handleChange(e, "olympusGridVersion")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="noOfNodes"
                                    type="text"
                                    label="No of Nodes"
                                    fullWidth
                                    value={state.noOfNodes}
                                    onChange={(e) => handleChange(e, "noOfNodes")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="releaseDate"
                                    type="text"
                                    label="Release Date"
                                    fullWidth
                                    value={state.releaseDate}
                                    onChange={(e) => handleChange(e, "releaseDate")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDInput
                                    name="privacySettings"
                                    type="text"
                                    label="Privacy Settings"
                                    fullWidth
                                    value={state.privacySettings}
                                    onChange={(e) => handleChange(e, "privacySettings")}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>
                <Card sx={{marginTop: "16px"}}>
                    <MDBox p={2}>
                        <MDTypography component="h6" variant="button" fontWeight="bold" color="dark" mb={2}>
                            AI Providers
                        </MDTypography>
                        <MDTypography component="p" variant="button" fontWeight="light" color="dark" mb={2}>
                            You can connect external AI models such as OpenAI, Grok, Claude or custom SDKs.
                        </MDTypography>
                        <MDButton variant="outlined" color="light">
                            Add AI Provider
                        </MDButton>
                    </MDBox>
                </Card>
                <Card sx={{marginTop: "16px"}}>
                    <MDBox p={2}>
                        <MDTypography component="h6" variant="button" fontWeight="bold" color="dark" mb={2}>
                            Agents
                        </MDTypography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Athena
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="success">
                                        Enabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Hermes
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Chronos
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Iris
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Orion
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Eos
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Hestia
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Apollo
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Ares
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Hephaestus
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Zeus
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" justifyContent="space-between" p={1} mt={1}>
                                    <MDBox textAlign="left" lineHeight={1.25}>
                                        <MDBox lineHeight={1.25}>
                                            <MDTypography variant="button" fontWeight="light" color="dark">
                                                Poseidon
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="light" color="text">
                                        Disabled
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <MDBox className="plugin-info" display="flex" alignItems="center" p={1} mt={1}>
                                    <MDButton variant="outlined" color="light">
                                        Find More Agents
                                    </MDButton>
                                    <MDButton variant="contained" color="dark" sx={{marginLeft: "8px"}}>
                                        Create Your Own Agent
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>
                <Card sx={{marginTop: "16px"}}>
                    <MDBox p={2}>
                        <MDTypography component="h6" variant="button" fontWeight="bold" color="dark" mb={2}>
                            Ethics
                        </MDTypography>
                        <ReactSortable
                            group={{
                                name: 'shared',
                                pull: 'clone',
                                put: false
                            }}
                            animation={150}
                            list={state.ethics}
                            sort={true}
                            onChoose={(event) => {
                                ethicRef.current = event;
                            }}
                            setList={(data) => {
                                let orderChanged = false;
                                state.ethics.map((ethic, index) => {
                                    if(ethic.title !== data[index].title){
                                        orderChanged = true;
                                    }
                                });
                                if(!orderChanged){
                                    return;
                                }
                                setState({ethics: data});
                            }}
                        >
                        
                            {renderEthics()}
                        </ReactSortable>
                        <MDBox mt={2}>
                            <MDButton variant="outlined" color="light">
                                Manage Ethics
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Card>    
            </MDBox>
        </DashboardLayout>
    )
}

export default Configuration;